<template>
  <ItemRow
    v-bind="$attrs"
    :class="$style.container"
    v-on="$listeners"
  >
    <template #content>
      <ItemWithLabel
        :class="$style.name"
        :column="columns[0]"
      >
        <ParagraphBody
          data-test-id="PolicyGroupTemplateItemRow_Name"
          :hasMargin="false"
          :title="itemData.name"
        >
          {{ itemData.name }}
        </ParagraphBody>
      </ItemWithLabel>
      <ItemWithLabel
        :class="$style.description"
        :column="columns[1]"
      >
        <ParagraphBody
          data-test-id="PolicyGroupTemplateItemRow_Description"
          :hasMargin="false"
          :title="itemData.description"
        >
          {{ itemData.description }}
        </ParagraphBody>
      </ItemWithLabel>
      <ItemWithLabel
        :class="$style.action"
        :column="columns[2]"
      >
        <ButtonSecondary
          :class="$style.createButton"
          data-test-id="PolicyGroupTemplateItemRow_CreateButton"
          :hasOutline="false"
          :isDisabled="isCreateButtonDisabled"
          :isLoading="false"
          :isMarketingStyle="false"
          :isMatchText="false"
          text="Create"
          @click="handleSaveAsPolicyGroup"
        />
      </ItemWithLabel>
    </template>
    <template #expandedContent>
      <ConfiguredPolicies
        data-test-id="PolicyGroupTemplateItemRow_ConfiguredPolicies"
        :tableData="itemData.policies"
      />
    </template>
  </ItemRow>
</template>

<script>
import {
  ButtonSecondary,
  ItemRow,
  ItemWithLabel,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  StoreModelMap,
} from 'client/util/Constants';

import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { get } from 'lodash';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import ConfiguredPolicies from 'client/groups/Configuration/ConfiguredPolicies.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';

const configurationGroupModel = StoreModelMap[ItemTypeConsts.configurationGroup];

const {
  description,
  location,
  section,
  text,
} = eventProperties;
const {
  policyGroupsPage,
} = eventLocations;

export default {
  name: 'PolicyGroupTemplateItemRow',

  components: {
    ButtonSecondary,
    ConfiguredPolicies,
    ItemRow,
    ItemWithLabel,
    ParagraphBody,
  },

  EventHelper,

  props: {
    columns: {
      type: Array,
      default: () => ([]),
    },

    itemData: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      isCreateButtonDisabled: false,
    };
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    ...mapActions(configurationGroupModel, [
      'createPolicyGroupFromTemplate',
    ]),

    async handleSaveAsPolicyGroup() {
      this.isCreateButtonDisabled = true;

      this.$options.EventHelper.triggerSaveTemplateAsPolicyGroupClicked({
        [description]: 'Save As Policy Group Button Clicked',
        [location]: policyGroupsPage,
        [section]: 'Footer',
        [text]: 'Save Policy Group',
      });

      try {
        const response = await this.createPolicyGroupFromTemplate(this.itemData);
        const { policies } = response.data;

        const hasPreexistingPolicy = policies.find(policy => policy.preexisting);

        if (hasPreexistingPolicy) {
          this.isCreateButtonDisabled = false;
          this.triggerSuccessNotification({
            message: 'Policy Group Template Saved Successfully! One or more policies included in the template already existed so no changes were made to their settings.',
            title: '',
            showCloseButton: true,
          });
          this.$router.push('/groups/configuration');
        } else {
          this.isCreateButtonDisabled = false;
          this.triggerSuccessNotification({
            message: 'Policy Group Template Saved Successfully!',
            title: '',
            showCloseButton: false,
          });
          this.$router.push('/groups/configuration');
        }
      } catch (err) {
        this.isCreateButtonDisabled = false;
        if (get(err, 'response.status') === 409) {
          this.triggerErrorNotification({
            message: 'Failed to save the Policy Group because a Group with the same name already exists.',
            title: 'Failed to Save',
            showCloseButton: true,
          });
        } else {
          this.triggerErrorNotification({
            message: 'Failed to Save the Policy Group',
            title: 'Failed to Save',
            showCloseButton: true,
          });
          this.$router.push('/groups/configuration');
        }
      }
    },
  },
};
</script>

<style module scoped>

.action {
  align-self: flex-start;
}

.name p,
.description p {
  -webkit-box-orient: vertical;
  display: -webkit-box;
  height: 3em;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
</style>
